import { useState, useCallback } from 'react';
import Card from './ui/Card';
import {
  getTA6Sections,
  countVisibleQuestions,
  countAnsweredQuestions,
  type TA6Question,
} from '../config/ta6Questions';
import {
  getTA7Sections,
  countVisibleTA7Questions,
  countAnsweredTA7Questions,
  type TA7Question,
} from '../config/ta7Questions';
import {
  getLPE1Sections,
  countVisibleLPE1Questions,
  countAnsweredLPE1Questions,
  type LPE1Question,
} from '../config/lpe1Questions';

interface DisclosureQuestionnaireProps {
  answers: Record<string, string>;
  onAnswersChange: (answers: Record<string, string>) => void;
  tenure: 'freehold' | 'leasehold';
}

type AnyQuestion = TA6Question | TA7Question | LPE1Question;
type AnySection = { section: number; title: string; questions: AnyQuestion[] };

// Sentinel written for questions skipped via the section-level "Skip section" button
// when the question type doesn't have a native "Not known" radio (i.e. yesNo, text,
// multiChoice). The backend formatter recognises 'not known' (case-insensitive) and
// passes it through to the PDF unchanged.
const NOT_KNOWN_TEXT = 'Not known';

function isNotKnownValue(question: AnyQuestion, value: string): boolean {
  if (question.answerType === 'yesNoNotKnown') return value === 'notKnown';
  return value === NOT_KNOWN_TEXT;
}

function QuestionField({ question, value, onChange, answers }: {
  question: AnyQuestion;
  value: string;
  onChange: (id: string, value: string) => void;
  answers: Record<string, string>;
}) {
  // Check conditional visibility
  if (question.conditionalOn) {
    const parentAnswer = answers[question.conditionalOn.questionId];
    if (parentAnswer !== question.conditionalOn.answer) return null;
  }

  const fieldId = `question-${question.id}`;
  const skippedNonNative =
    question.answerType !== 'yesNoNotKnown' && value === NOT_KNOWN_TEXT;

  return (
    <div className="py-3 border-b border-slate-100 last:border-b-0">
      <label htmlFor={fieldId} className="text-sm font-medium text-slate-950 block">
        {question.questionText}
      </label>
      {question.helpText && (
        <p className="text-xs text-slate-600 mt-1" id={`${fieldId}-help`}>
          {question.helpText}
        </p>
      )}

      <div className="mt-2">
        {skippedNonNative && (
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
            <span>Marked &ldquo;Not known&rdquo;</span>
            <button
              type="button"
              onClick={() => onChange(question.id, '')}
              className="text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
              aria-label={`Clear "Not known" for ${question.questionText}`}
            >
              Answer instead
            </button>
          </div>
        )}

        {(question.answerType === 'yesNoNotKnown' || question.answerType === 'yesNo') && (
          <fieldset aria-describedby={question.helpText ? `${fieldId}-help` : undefined}>
            <legend className="sr-only">{question.questionText}</legend>
            <div className="flex gap-3 flex-wrap">
              {['yes', 'no', ...(question.answerType === 'yesNoNotKnown' ? ['notKnown'] : [])].map(opt => (
                <label key={opt} className="inline-flex items-center">
                  <input
                    type="radio"
                    name={fieldId}
                    value={opt}
                    checked={value === opt}
                    onChange={() => onChange(question.id, opt)}
                    className="mr-1.5 accent-emerald-700"
                  />
                  <span className="text-sm text-slate-800">
                    {opt === 'notKnown' ? 'Not known' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {question.answerType === 'text' && (
          <textarea
            id={fieldId}
            value={skippedNonNative ? '' : value}
            onChange={e => onChange(question.id, e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
            aria-describedby={question.helpText ? `${fieldId}-help` : undefined}
          />
        )}

        {question.answerType === 'multiChoice' && question.choices && (
          <fieldset aria-describedby={question.helpText ? `${fieldId}-help` : undefined}>
            <legend className="sr-only">{question.questionText}</legend>
            <div className="flex gap-3 flex-wrap">
              {question.choices.map(choice => (
                <label key={choice} className="inline-flex items-center">
                  <input
                    type="radio"
                    name={fieldId}
                    value={choice}
                    checked={value === choice}
                    onChange={() => onChange(question.id, choice)}
                    className="mr-1.5 accent-emerald-700"
                  />
                  <span className="text-sm text-slate-800">{choice}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </div>
    </div>
  );
}

function getVisibleQuestions(section: AnySection, answers: Record<string, string>): AnyQuestion[] {
  return section.questions.filter(q => {
    if (!q.conditionalOn) return true;
    return answers[q.conditionalOn.questionId] === q.conditionalOn.answer;
  });
}

function SectionAccordion({ section, answers, onAnswer, onSkipSection, onUndoSkipSection, isOpen, onToggle }: {
  section: AnySection;
  answers: Record<string, string>;
  onAnswer: (id: string, value: string) => void;
  onSkipSection: (section: AnySection) => void;
  onUndoSkipSection: (section: AnySection) => void;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const visibleQuestions = getVisibleQuestions(section, answers);
  const answeredInSection = visibleQuestions.filter(q => !!answers[q.id]).length;
  const visibleInSection = visibleQuestions.length;
  const skippedInSection = visibleQuestions.filter(q => isNotKnownValue(q, answers[q.id] || '')).length;
  const isFullySkipped = skippedInSection > 0 && skippedInSection === visibleInSection;
  const hasUnanswered = answeredInSection < visibleInSection;
  const sectionLabel = `${section.section}. ${section.title}`;

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={onToggle}
          className="flex-1 text-left flex items-start gap-3 -m-6 p-6 pr-3"
          aria-expanded={isOpen}
          aria-controls={`section-${section.section}`}
        >
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-950">
              {sectionLabel}
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              {isFullySkipped
                ? 'Section skipped — solicitor will follow up'
                : `${answeredInSection} of ${visibleInSection} answered`}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 pt-0.5">
            {answeredInSection === visibleInSection && visibleInSection > 0 && !isFullySkipped && (
              <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
            <svg
              className={`w-5 h-5 text-slate-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isFullySkipped ? (
          <button
            type="button"
            onClick={() => onUndoSkipSection(section)}
            className="shrink-0 -mr-2 -mt-2 text-xs font-medium text-emerald-700 hover:text-emerald-800 underline underline-offset-2 px-2 py-1"
            aria-label={`Undo skip for ${sectionLabel}`}
          >
            Undo skip
          </button>
        ) : hasUnanswered && (
          <button
            type="button"
            onClick={() => onSkipSection(section)}
            className="shrink-0 -mr-2 -mt-2 text-xs font-medium text-slate-700 hover:text-slate-900 underline underline-offset-2 px-2 py-1"
            aria-label={`Skip ${sectionLabel} — solicitor will follow up`}
          >
            Skip section
          </button>
        )}
      </div>

      {isOpen && (
        <div id={`section-${section.section}`} className="mt-4 pt-4 border-t border-slate-200">
          {section.questions.map(q => (
            <QuestionField
              key={q.id}
              question={q}
              value={answers[q.id] || ''}
              onChange={onAnswer}
              answers={answers}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

export default function DisclosureQuestionnaire({
  answers,
  onAnswersChange,
  tenure,
}: DisclosureQuestionnaireProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['ta6-1']));
  const ta6Sections = getTA6Sections();
  const ta7Sections = tenure === 'leasehold' ? getTA7Sections() : [];
  const lpe1Sections = tenure === 'leasehold' ? getLPE1Sections() : [];

  // Total progress across all forms
  const ta6Visible = countVisibleQuestions(answers);
  const ta6Answered = countAnsweredQuestions(answers);
  const ta7Visible = tenure === 'leasehold' ? countVisibleTA7Questions(answers) : 0;
  const ta7Answered = tenure === 'leasehold' ? countAnsweredTA7Questions(answers) : 0;
  const lpe1Visible = tenure === 'leasehold' ? countVisibleLPE1Questions(answers) : 0;
  const lpe1Answered = tenure === 'leasehold' ? countAnsweredLPE1Questions(answers) : 0;
  const totalVisible = ta6Visible + ta7Visible + lpe1Visible;
  const totalAnswered = ta6Answered + ta7Answered + lpe1Answered;

  const handleAnswer = useCallback((id: string, value: string) => {
    onAnswersChange({ ...answers, [id]: value });
  }, [answers, onAnswersChange]);

  const handleSkipSection = useCallback((section: AnySection) => {
    const next = { ...answers };
    for (const q of getVisibleQuestions(section, answers)) {
      if (next[q.id]) continue;
      next[q.id] = q.answerType === 'yesNoNotKnown' ? 'notKnown' : NOT_KNOWN_TEXT;
    }
    onAnswersChange(next);
  }, [answers, onAnswersChange]);

  const handleUndoSkipSection = useCallback((section: AnySection) => {
    const next = { ...answers };
    for (const q of getVisibleQuestions(section, answers)) {
      if (isNotKnownValue(q, next[q.id] || '')) {
        delete next[q.id];
      }
    }
    onAnswersChange(next);
  }, [answers, onAnswersChange]);

  const toggleSection = useCallback((key: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return (
    <div className="space-y-4">
      {/* Reassurance — sits ABOVE the progress bar so the "Not known is OK"
          message lands before the user sees a question count that might
          feel daunting. */}
      <Card variant="subtle" padding="sm">
        <p className="text-sm text-slate-700">
          Answer what you can. Questions marked &ldquo;Not known&rdquo; are acceptable &mdash; your solicitor can follow up.
        </p>
        <p className="mt-2 text-sm text-slate-700">
          Don&rsquo;t have the information for a whole section? Use &ldquo;Skip section&rdquo; on its header to mark every question &ldquo;Not known&rdquo; at once.
        </p>
        <p className="mt-2 text-sm text-slate-700">
          Your draft saves automatically. You can close this tab and come back from your account.
        </p>
      </Card>

      {/* Progress summary */}
      <Card variant="subtle" padding="sm">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-700">
            {totalAnswered} of {totalVisible} questions answered
            {tenure === 'leasehold' && ' (TA6 + TA7 + LPE1)'}
          </p>
          <span className="text-sm font-medium text-slate-950">
            {totalVisible > 0 ? Math.round((totalAnswered / totalVisible) * 100) : 0}%
          </span>
        </div>
        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mt-2">
          <div
            className="h-full bg-emerald-600 transition-all duration-300 rounded-full"
            style={{ width: `${totalVisible > 0 ? (totalAnswered / totalVisible) * 100 : 0}%` }}
            role="progressbar"
            aria-valuenow={totalAnswered}
            aria-valuemin={0}
            aria-valuemax={totalVisible}
            aria-label="Questionnaire completion"
          />
        </div>
      </Card>

      {/* TA6 sections */}
      <h3 className="text-base font-semibold text-slate-950 pt-2">TA6 — Property Information Form</h3>
      {ta6Sections.map(section => (
        <SectionAccordion
          key={`ta6-${section.section}`}
          section={section}
          answers={answers}
          onAnswer={handleAnswer}
          onSkipSection={handleSkipSection}
          onUndoSkipSection={handleUndoSkipSection}
          isOpen={openSections.has(`ta6-${section.section}`)}
          onToggle={() => toggleSection(`ta6-${section.section}`)}
        />
      ))}

      {/* TA7 sections (leasehold only) */}
      {tenure === 'leasehold' && ta7Sections.length > 0 && (
        <>
          <h3 className="text-base font-semibold text-slate-950 pt-4">TA7 — Leasehold Information Form</h3>
          <Card variant="subtle" padding="sm">
            <p className="text-sm text-slate-700">
              These questions apply to leasehold properties only. They cover your lease terms, service charges, and management arrangements.
            </p>
          </Card>
          {ta7Sections.map(section => (
            <SectionAccordion
              key={`ta7-${section.section}`}
              section={section}
              answers={answers}
              onAnswer={handleAnswer}
              onSkipSection={handleSkipSection}
              onUndoSkipSection={handleUndoSkipSection}
              isOpen={openSections.has(`ta7-${section.section}`)}
              onToggle={() => toggleSection(`ta7-${section.section}`)}
            />
          ))}
        </>
      )}

      {/* LPE1 sections (leasehold only) */}
      {tenure === 'leasehold' && lpe1Sections.length > 0 && (
        <>
          <h3 className="text-base font-semibold text-slate-950 pt-4">LPE1 — Leasehold Property Enquiries</h3>
          <Card variant="subtle" padding="sm">
            <p className="text-sm text-slate-700">
              LPE1 is normally completed by your managing agent or freeholder. Pre-fill what you know to speed up the process.
            </p>
          </Card>
          {lpe1Sections.map(section => (
            <SectionAccordion
              key={`lpe1-${section.section}`}
              section={section}
              answers={answers}
              onAnswer={handleAnswer}
              onSkipSection={handleSkipSection}
              onUndoSkipSection={handleUndoSkipSection}
              isOpen={openSections.has(`lpe1-${section.section}`)}
              onToggle={() => toggleSection(`lpe1-${section.section}`)}
            />
          ))}
        </>
      )}
    </div>
  );
}
